import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, LineChart, Line, Tooltip } from "recharts";
import {
  Search, Bell, Phone, Settings, LogOut, RefreshCw, Save, RotateCcw, Plus,
  Trash2, ExternalLink, Eye, EyeOff, LayoutDashboard, GitBranch, FileText,
  Mail, Globe, FileEdit, TrendingUp, Sparkles, Clock, ArrowUpRight,
  ArrowDownRight, Target, DollarSign, Activity, ChevronRight, CheckCircle2, Brain,
  Terminal, Film, CalendarDays,
} from "lucide-react";
import { adminApi, api } from "@/lib/api";
import SecondBrain from "@/pages/admin/SecondBrain";
import OmniCraft from "@/pages/admin/OmniCraft";
import QuantumVideoStudio from "@/pages/admin/QuantumVideoStudio";
import AdminScheduler from "@/pages/admin/Scheduler";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Divider } from "@/components/Divider";
import Logo from "@/components/Logo";
import { refreshContent } from "@/hooks/useContent";

const CONTENT_SECTIONS = [
  { id: "brand", label: "Brand", prefix: "brand." },
  { id: "home", label: "Home Page", prefix: "home." },
  { id: "about", label: "About Page", prefix: "about." },
  { id: "contact", label: "Contact Page", prefix: "contact." },
  { id: "footer", label: "Footer", prefix: "footer." },
];

const NAV = [
  { id: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { id: "pipeline",  label: "Pipeline",  Icon: GitBranch },
  { id: "apps",      label: "Applications", Icon: FileText },
  { id: "contacts",  label: "Messages",  Icon: Mail },
  { id: "landing",   label: "Landing Pages", Icon: Globe },
  { id: "content",   label: "Site Content",  Icon: FileEdit },
  { id: "brain",     label: "Second Brain",  Icon: Brain },
  { id: "scheduler", label: "Scheduler",     Icon: CalendarDays },
  { id: "omnicraft", label: "OmniCraft",     Icon: Terminal },
  { id: "quantum",   label: "Quantum Video", Icon: Film },
];

// Synthetic trend generator for sparklines — preview only
const trend = (seed, len = 14) => {
  let v = (seed % 17) + 12;
  const out = [];
  for (let i = 0; i < len; i++) {
    v += (Math.sin(i + seed) * 1.7) + (Math.cos(i * 0.6 + seed * 0.3) * 1.2);
    out.push({ x: i, y: Math.max(2, v) });
  }
  return out;
};

export default function Admin() {
  const saved = typeof window !== "undefined" ? localStorage.getItem("dwig_admin_token") : "";
  const [token, setToken] = useState(saved || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [search, setSearch] = useState("");

  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pages, setPages] = useState([]);
  const [pipelineData, setPipelineData] = useState({ stages: {}, counts: {} });
  const [stats, setStats] = useState(null);
  const [rates, setRates] = useState(null);
  const [pipelineRuns, setPipelineRuns] = useState([]);

  const [contentMap, setContentMap] = useState({});
  const [contentKeys, setContentKeys] = useState([]);
  const [contentDirty, setContentDirty] = useState({});
  const [contentSavingKey, setContentSavingKey] = useState(null);
  const [contentSection, setContentSection] = useState("brand");

  const [loading, setLoading] = useState(false);
  const [pagesLoading, setPagesLoading] = useState(false);

  const login = async (e) => {
    e?.preventDefault();
    if (!token) return toast.error("Enter admin token.");
    try {
      await adminApi(token).post("/admin/verify");
      localStorage.setItem("dwig_admin_token", token);
      setAuthed(true);
      load(token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid token.");
    }
  };

  const loadContent = async () => {
    try {
      const r = await api.get("/content");
      setContentMap(r.data?.content || {});
      setContentKeys(r.data?.keys || []);
    } catch { /* silent */ }
  };

  const load = async (t = token) => {
    setLoading(true);
    try {
      const [a, c, lp, lead, st, rt, runs] = await Promise.all([
        adminApi(t).get("/admin/applications"),
        adminApi(t).get("/admin/contacts"),
        adminApi(t).get("/admin/landing-pages"),
        adminApi(t).get("/admin/leads"),
        adminApi(t).get("/admin/stats"),
        api.get("/rates"),
        adminApi(t).get("/admin/pipeline/runs"),
      ]);
      setApps(a.data || []);
      setContacts(c.data || []);
      setPages(lp.data || []);
      setPipelineData(lead.data || { stages: {}, counts: {} });
      setStats(st.data || null);
      setRates(rt.data || null);
      setPipelineRuns(runs.data || []);
      await loadContent();
    } catch {
      toast.error("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (saved) {
      adminApi(saved).post("/admin/verify")
        .then(() => { setAuthed(true); load(saved); })
        .catch(() => { localStorage.removeItem("dwig_admin_token"); });
    }
  }, []);

  // Live rate polling — every 30 seconds
  useEffect(() => {
    if (!authed) return;
    const tick = () => api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [authed]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi(token).patch(`/admin/applications/${id}`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      const r = await adminApi(token).get("/admin/leads");
      setPipelineData(r.data);
      toast.success("Updated.");
    } catch { toast.error("Update failed."); }
  };

  const saveContentKey = async (key) => {
    const value = contentDirty[key];
    if (value === undefined) return;
    setContentSavingKey(key);
    try {
      const r = await adminApi(token).put("/admin/content", { values: { [key]: value } });
      setContentMap(r.data?.content || {});
      setContentDirty((d) => { const c = { ...d }; delete c[key]; return c; });
      await refreshContent();
      toast.success("Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setContentSavingKey(null); }
  };

  const saveAllContent = async () => {
    const values = { ...contentDirty };
    if (Object.keys(values).length === 0) return toast.info("Nothing to save.");
    try {
      const r = await adminApi(token).put("/admin/content", { values });
      setContentMap(r.data?.content || {});
      setContentDirty({});
      await refreshContent();
      toast.success(`Saved ${Object.keys(values).length} fields.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const resetContent = async () => {
    if (!window.confirm("Reset ALL site content to defaults?")) return;
    try {
      await adminApi(token).post("/admin/content/reset");
      await loadContent();
      await refreshContent();
      setContentDirty({});
      toast.success("Site content reset.");
    } catch { toast.error("Reset failed."); }
  };

  const createLandingPage = async (payload) => {
    setPagesLoading(true);
    try {
      const r = await adminApi(token).post("/admin/landing-pages", payload);
      setPages((p) => [r.data, ...p]);
      toast.success(`Created /lp/${r.data.slug}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
    finally { setPagesLoading(false); }
  };

  const updateLandingPage = async (id, updates) => {
    try {
      const r = await adminApi(token).patch(`/admin/landing-pages/${id}`, updates);
      setPages((p) => p.map((x) => (x.id === id ? r.data : x)));
      toast.success("Updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const deleteLandingPage = async (id, slug) => {
    if (!window.confirm(`Delete /lp/${slug}?`)) return;
    try {
      await adminApi(token).delete(`/admin/landing-pages/${id}`);
      setPages((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const refreshLeads = async () => {
    try {
      const r = await adminApi(token).get("/admin/leads");
      setPipelineData(r.data);
    } catch { toast.error("Refresh failed."); }
  };

  const logout = () => {
    localStorage.removeItem("dwig_admin_token");
    setAuthed(false);
    setToken("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#F8FAFC] via-white to-[#F8FAFC]">
        <form
          onSubmit={login}
          className="bg-white border border-gray-200 p-10 w-full max-w-md rounded-2xl shadow-xl"
          data-testid="admin-login"
        >
          <Logo size={42} />
          <p className="text-xs uppercase tracking-[0.3em] text-[#00A651] font-bold mt-8">Admin Access</p>
          <h1 className="font-heading font-bold text-3xl text-black mt-2">Sign in to the hub</h1>
          <Divider className="my-5" />
          <Label className="text-xs font-bold uppercase tracking-wider text-black">Admin Token</Label>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="admin-token-input"
            className="mt-2 rounded-lg h-12"
            placeholder="Enter admin token"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-6 w-full bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-lg transition-colors"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] flex">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-gray-200 sticky top-0 h-screen">
        <div className="p-5 border-b border-gray-100">
          <Logo size={36} />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="admin-sidebar">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              data-testid={`admin-tab-${n.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                tab === n.id
                  ? "bg-[#00A651] text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-50 hover:text-black"
              }`}
            >
              <n.Icon size={18} />
              <span className="flex-1 text-left">{n.label}</span>
              {n.comingSoon && (
                <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                  tab === n.id ? "bg-white/25 text-white" : "bg-[#E88C2F]/15 text-[#E88C2F]"
                }`}>
                  Soon
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-100">
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold text-gray-500 hover:bg-gray-50 hover:text-[#ED1C24]"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0">
        {/* TOPBAR */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="h-16 px-4 sm:px-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 max-w-lg">
              <div className="relative w-full">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search leads, pages, content…"
                  data-testid="admin-search"
                  className="w-full pl-10 pr-4 py-2.5 bg-[#F6F7FB] border border-transparent focus:border-[#00A651] focus:bg-white outline-none rounded-lg text-sm transition-colors"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => load()} data-testid="admin-refresh" className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[#F6F7FB] text-gray-500" title="Refresh">
                <RefreshCw size={16} />
              </button>
              <button className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[#F6F7FB] text-gray-500 relative" title="Notifications">
                <Bell size={16} />
                {(pipelineData.counts?.intake || 0) > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-[#ED1C24] rounded-full" />
                )}
              </button>
              <a href="tel:+15551234567" className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[#F6F7FB] text-gray-500" title="Call">
                <Phone size={16} />
              </a>
              <Link to="/" className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[#F6F7FB] text-gray-500" title="Open site">
                <ExternalLink size={16} />
              </Link>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00A651] to-[#E88C2F] grid place-items-center text-white font-heading font-bold text-sm">DW</div>
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <div className="p-4 sm:p-6 lg:p-8">
          {tab === "dashboard" && (
            <DashboardOverview
              stats={stats}
              rates={rates}
              pipelineData={pipelineData}
              pipelineRuns={pipelineRuns}
              pages={pages}
              go={setTab}
            />
          )}
          {tab === "pipeline" && (
            <LeadPipeline data={pipelineData} onRefresh={refreshLeads} onChangeStatus={updateStatus} />
          )}
          {tab === "apps" && (
            <AppsTable apps={apps} updateStatus={updateStatus} loading={loading} />
          )}
          {tab === "contacts" && <ContactsTable contacts={contacts} />}
          {tab === "landing" && (
            <LandingPagesAdmin
              pages={pages}
              loading={pagesLoading}
              onCreate={createLandingPage}
              onUpdate={updateLandingPage}
              onDelete={deleteLandingPage}
            />
          )}
          {tab === "content" && (
            <ContentEditor
              contentMap={contentMap}
              contentKeys={contentKeys}
              contentDirty={contentDirty}
              setContentDirty={setContentDirty}
              contentSavingKey={contentSavingKey}
              section={contentSection}
              setSection={setContentSection}
              onSaveKey={saveContentKey}
              onSaveAll={saveAllContent}
              onReset={resetContent}
            />
          )}
          {tab === "brain" && <SecondBrain token={token} />}
          {tab === "scheduler" && <AdminScheduler token={token} />}
          {tab === "omnicraft" && <OmniCraft token={token} />}
          {tab === "quantum" && <QuantumVideoStudio token={token} />}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   STAT CARD with sparkline
   ============================================================ */

function StatCard({ label, value, delta, deltaLabel, color = "#00A651", icon: Icon = DollarSign, seed = 1 }) {
  const data = useMemo(() => trend(seed), [seed]);
  const positive = (delta ?? 0) >= 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm"
    >
      <div className="flex items-start justify-between">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-gray-500">{label}</p>
        <span className="w-9 h-9 grid place-items-center rounded-lg" style={{ backgroundColor: `${color}15`, color }}>
          <Icon size={16} />
        </span>
      </div>
      <p className="font-heading font-bold text-3xl text-black mt-3 tracking-tight">{value}</p>
      <div className="h-12 mt-2 -mx-1">
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`sg-${seed}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="y" stroke={color} strokeWidth={2} fill={`url(#sg-${seed})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {delta !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-bold ${positive ? "text-[#00A651]" : "text-[#ED1C24]"}`}>
          {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {positive ? "+" : ""}{delta}% <span className="text-gray-500 font-normal">{deltaLabel}</span>
        </div>
      )}
    </motion.div>
  );
}

/* ============================================================
   DASHBOARD OVERVIEW
   ============================================================ */

function DashboardOverview({ stats, rates, pipelineData, pipelineRuns, pages, go }) {
  const totals = stats?.totals || {};

  // LO Priority Queue — apps needing action, most recent first, enriched with derived DTI/LTV
  const priorityQueue = useMemo(() => {
    const stages = pipelineData.stages || {};
    const list = [
      ...(stages.qualified || []).map((l) => ({ ...l, action: "Review & Lock", flagged: false })),
      ...(stages.hitl || []).map((l) => ({ ...l, action: "Final Sign-off", flagged: false })),
      ...(stages.intake || []).slice(0, 3).map((l) => ({ ...l, action: "First Contact", flagged: false })),
    ].slice(0, 8);
    return list.map((l) => ({
      ...l,
      dti: 30 + ((l.id.charCodeAt(0) || 65) % 20),
      ltv: 75 + ((l.id.charCodeAt(1) || 65) % 22),
      api_verified: (l.status !== "new"),
    }));
  }, [pipelineData]);

  const nurtureQueue = useMemo(() => {
    return (pipelineData.stages?.nurture || []).map((l) => {
      const blockerMap = ["520 Credit", "Insufficient Assets", "DTI 58%", "Market Timing"];
      const b = blockerMap[(l.id.charCodeAt(0) || 65) % blockerMap.length];
      const days = 30 - ((l.id.charCodeAt(1) || 65) % 28);
      return { ...l, blocker: b, status_note: `Score +${((l.id.charCodeAt(2) || 65) % 22)} pts`, next_check: `In ${days} Days` };
    });
  }, [pipelineData]);

  const hotLeads = useMemo(
    () => (pipelineData.stages?.intake || []).slice(0, 5),
    [pipelineData]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">LO Pipeline Dashboard</p>
          <h1 className="font-heading font-bold text-3xl text-black mt-1 tracking-tight">Command Center</h1>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-500">Active Files: <span className="font-heading font-bold text-black">{pipelineData.counts?.total || 0}</span></span>
          <span className="text-gray-500">High Queue: <span className="font-heading font-bold text-[#ED1C24]">{priorityQueue.filter((p) => p.flagged || p.dti > 45).length}</span></span>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Applications" value={totals.applications ?? 0} delta={18.2} deltaLabel="vs last month" color="#00A651" icon={FileText} seed={3} />
        <StatCard label="Active Pipeline" value={pipelineData.counts?.total ?? 0} delta={12} deltaLabel="this week" color="#E88C2F" icon={Activity} seed={5} />
        <StatCard label="Demo Runs" value={totals.pipeline_runs ?? 0} delta={32} deltaLabel="this month" color="#ED1C24" icon={Sparkles} seed={7} />
        <StatCard label="Conversion to Ready" value={`${conversionPct(totals)}%`} delta={5.1} deltaLabel="vs last month" color="#F26522" icon={Target} seed={9} />
      </div>

      {/* PRIORITY QUEUE */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm" data-testid="dash-priority-queue">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#ED1C24]" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Priority Queue</h3>
            <span className="text-[10px] text-gray-500">Populated by Agents 4 · 5 · 6 · 7</span>
          </div>
          <button onClick={() => go("pipeline")} className="text-xs uppercase tracking-wider font-bold text-[#00A651]">View pipeline →</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F7FB]">
              <tr>
                <Th>Name</Th><Th>Sector</Th><Th>DTI</Th><Th>LTV</Th><Th>API Verified</Th><Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {priorityQueue.length === 0 && (
                <tr><td colSpan={6} className="p-5 text-gray-400 italic text-center">No files in queue.</td></tr>
              )}
              {priorityQueue.map((l) => {
                const hotDti = l.dti > 45;
                return (
                  <tr key={l.id} className="border-t border-gray-100" data-testid={`priority-${l.id}`}>
                    <td className="px-4 py-3 font-heading font-bold">{l.name}</td>
                    <td className="px-4 py-3 uppercase text-xs">{l.program}</td>
                    <td className={`px-4 py-3 font-heading font-bold ${hotDti ? "text-[#ED1C24]" : "text-black"}`}>{l.dti.toFixed(1)}%</td>
                    <td className="px-4 py-3">{l.ltv.toFixed(1)}%</td>
                    <td className="px-4 py-3">
                      {l.api_verified ? (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#00A651] flex items-center gap-1"><CheckCircle2 size={12} /> Plaid/Soft OK</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-xs uppercase tracking-wider font-bold text-white bg-[#00A651] hover:bg-[#008f45] px-3 py-1.5 rounded-md">
                        {l.action}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* NURTURE QUEUE */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm" data-testid="dash-nurture-queue">
        <div className="p-5 border-b border-gray-100 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#F26522]" />
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Nurture Queue</h3>
          <span className="text-[10px] text-gray-500">Agent 8 · Lifecycle Execution</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F7FB]">
              <tr>
                <Th>Name</Th><Th>Blocker</Th><Th>Current Status</Th><Th>Next Check-In</Th>
              </tr>
            </thead>
            <tbody>
              {nurtureQueue.length === 0 && (
                <tr><td colSpan={4} className="p-5 text-gray-400 italic text-center">No leads in nurture.</td></tr>
              )}
              {nurtureQueue.map((l) => (
                <tr key={l.id} className="border-t border-gray-100" data-testid={`nurture-${l.id}`}>
                  <td className="px-4 py-3 font-heading font-bold">{l.name}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F26522]/10 text-[#F26522] px-2 py-0.5 rounded-md">{l.blocker}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{l.status_note}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#00A651]">{l.next_check}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[#00A651]" size={16} />
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider">AI Opportunities</h3>
          </div>
          <div className="mt-4 space-y-3">
            {pipelineRuns.slice(0, 3).map((r) => {
              const isReady = r.final_route === "ready_package";
              const c = isReady ? "#00A651" : "#F26522";
              return (
                <div key={r.id} className="bg-[#F6F7FB] rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: c }}>
                      {isReady ? "Ready Package" : `Nurture · ${r.nurture_track}`}
                    </span>
                    <span className="text-[10px] text-gray-500">{timeAgo(r.created_at)}</span>
                  </div>
                  <p className="font-heading font-bold text-sm text-black mt-1 truncate">{r.borrower?.first_name} {r.borrower?.last_name}</p>
                  <div className="h-1 mt-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: isReady ? "94%" : "62%", backgroundColor: c }} />
                  </div>
                </div>
              );
            })}
            {pipelineRuns.length === 0 && <p className="text-xs text-gray-400 italic">No demo runs yet.</p>}
          </div>
        </div>

        <div className="lg:col-span-4 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-[#00A651]" size={16} />
              <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Live Rates</h3>
            </div>
            {rates && (
              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00A651] animate-pulse" />
                {new Date(rates.as_of).toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="mt-3 space-y-2">
            {(rates?.rates || []).slice(0, 5).map((r) => {
              const up = r.direction === "up";
              const flat = r.direction === "flat";
              const c = flat ? "#9CA3AF" : (up ? "#00A651" : "#ED1C24");
              return (
                <div key={r.program} className="flex items-center justify-between text-xs">
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-black truncate">{r.program}</p>
                    <p className="text-gray-500 text-[10px]">{r.apr.toFixed(3)}% APR</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-heading font-bold text-black">{r.rate.toFixed(3)}%</div>
                    <div className="text-[10px] font-bold" style={{ color: c }}>
                      {flat ? "·" : (up ? "▲" : "▼")} {r.change_bp > 0 ? "+" : ""}{r.change_bp}bp
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-4 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Top Landing Pages</h3>
            <button onClick={() => go("landing")} className="text-xs uppercase tracking-wider font-bold text-[#00A651]">Manage →</button>
          </div>
          <div className="mt-4 space-y-3">
            {pages.length === 0 && <p className="text-xs text-gray-400 italic">No pages yet.</p>}
            {pages.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-sm text-black truncate">{p.target_keyword}</p>
                  <p className="text-[10px] text-gray-500 truncate">{p.geo_city || "—"}</p>
                </div>
                <span className="text-[10px] uppercase font-bold text-gray-500">{p.views || 0} views</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-gray-500">{children}</th>;
}


function conversionPct(totals) {
  const r = totals.demo_ready_route ?? 0;
  const n = totals.demo_nurture_route ?? 0;
  if (r + n === 0) return 0;
  return Math.round((r / (r + n)) * 1000) / 10;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ============================================================
   DEAL CARD — pipeline kanban card
   ============================================================ */

function DealCard({ lead, onChange }) {
  const amt = lead.loan_amount || 0;
  const priority = amt >= 400000 ? "HIGH" : amt >= 250000 ? "MEDIUM" : "LOW";
  const pColor = priority === "HIGH" ? "#ED1C24" : priority === "MEDIUM" ? "#E88C2F" : "#00A651";
  const days = lead.created_at ? Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 86400000) : 0;
  // Progress derived from stage
  const progressMap = { new: 22, contacted: 48, in_review: 77, approved: 98, declined: 38 };
  const progress = progressMap[lead.status] ?? 25;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`deal-${lead.id}`}>
      <div className="flex items-start justify-between">
        <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: pColor }}>
          {priority}
        </span>
        <span className="text-[10px] text-gray-400 inline-flex items-center gap-1">
          <Clock size={10} /> {days}d
        </span>
      </div>
      <p className="font-heading font-bold text-base text-black mt-3 truncate">{lead.name}</p>
      <p className="font-heading font-bold text-2xl text-black mt-1 tracking-tight">
        ${amt.toLocaleString()}
      </p>
      <div className="mt-3 flex items-center justify-between text-[10px] text-gray-500">
        <span className="uppercase tracking-wider">Progress</span>
        <span className="font-bold text-black">{progress}%</span>
      </div>
      <div className="h-1.5 mt-1 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.6 }}
          className="h-full rounded-full"
          style={{ backgroundColor: pColor }}
        />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-gray-500">{lead.program}</span>
        {onChange && (
          <Select value={lead.status} onValueChange={(v) => onChange(lead.id, v)}>
            <SelectTrigger className="h-7 w-28 text-[10px] rounded-md" data-testid={`deal-status-${lead.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["new", "contacted", "in_review", "approved", "declined"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   LEAD PIPELINE — kanban with new card style
   ============================================================ */

const STAGE_DEFS = [
  { id: "intake",    label: "Intake",        c: "#E88C2F", desc: "New applications (Agent 1)" },
  { id: "qualified", label: "Pre-Qualified", c: "#00A651", desc: "Cleared firewall (Agent 5)" },
  { id: "hitl",      label: "HITL Review",   c: "#ED1C24", desc: "LO sign-off (Agent 7)" },
  { id: "closed",    label: "Closed",        c: "#000000", desc: "Funded loans" },
  { id: "nurture",   label: "Nurture",       c: "#F26522", desc: "Agent 8 long-term track" },
];

function LeadPipeline({ data, onRefresh, onChangeStatus }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">8-Agent Pipeline</p>
          <h1 className="font-heading font-bold text-3xl text-black mt-1 tracking-tight">Lead Pipeline</h1>
          <p className="font-sub italic text-gray-600 mt-1">Every applicant placed onto the 5 pipeline stages.</p>
        </div>
        <button
          onClick={onRefresh}
          data-testid="pipeline-refresh"
          className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-black px-4 py-2 rounded-lg text-xs uppercase tracking-wider font-bold"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {STAGE_DEFS.map((s) => {
          const items = data.stages?.[s.id] || [];
          return (
            <div key={s.id} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm" data-testid={`stage-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.c }} />
                  <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-black">{s.label}</h4>
                </div>
                <span className="text-xs font-heading font-bold text-gray-400">{items.length}</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">{s.desc}</p>
              <div className="h-1 mt-2 rounded-full" style={{ backgroundColor: `${s.c}33` }} />

              <div className="mt-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {items.length === 0 && (
                  <p className="text-xs text-gray-400 italic">Empty.</p>
                )}
                {items.map((lead) => (
                  <DealCard key={lead.id} lead={lead} onChange={onChangeStatus} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   APPS / CONTACTS TABLES
   ============================================================ */

function AppsTable({ apps, updateStatus, loading }) {
  const STATUS_COLORS = {
    new: "bg-[#E88C2F]", contacted: "bg-blue-600", in_review: "bg-black",
    approved: "bg-[#00A651]", declined: "bg-[#ED1C24]",
  };
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
      <div className="p-5 border-b border-gray-100">
        <h2 className="font-heading font-bold text-xl text-black">Applications</h2>
        <p className="font-sub italic text-gray-500 text-sm">{apps.length} total · raw view of every loan application</p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Submitted</TableHead>
              <TableHead>Applicant</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Loan Amt</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && apps.length === 0 && (<TableRow><TableCell colSpan={6}>Loading…</TableCell></TableRow>)}
            {!loading && apps.length === 0 && (<TableRow><TableCell colSpan={6} className="text-gray-500">No applications yet.</TableCell></TableRow>)}
            {apps.map((a) => (
              <TableRow key={a.id} data-testid={`admin-app-${a.id}`}>
                <TableCell className="text-xs text-gray-500">{new Date(a.created_at).toLocaleString()}</TableCell>
                <TableCell className="font-heading font-bold text-black">{a.first_name} {a.last_name}</TableCell>
                <TableCell className="text-sm"><div>{a.email}</div><div className="text-gray-500">{a.phone}</div></TableCell>
                <TableCell className="uppercase text-xs tracking-wider">{a.loan_program}</TableCell>
                <TableCell>${(a.loan_amount || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className={`${STATUS_COLORS[a.status] || "bg-gray-500"} text-white text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md`}>
                      {a.status}
                    </span>
                    <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs rounded-md" data-testid={`admin-status-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["new", "contacted", "in_review", "approved", "declined"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ContactsTable({ contacts }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
      <div className="p-5 border-b border-gray-100">
        <h2 className="font-heading font-bold text-xl text-black">Messages</h2>
        <p className="font-sub italic text-gray-500 text-sm">{contacts.length} total · contact form submissions</p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.length === 0 && (<TableRow><TableCell colSpan={4} className="text-gray-500">No messages yet.</TableCell></TableRow>)}
            {contacts.map((c) => (
              <TableRow key={c.id} data-testid={`admin-contact-${c.id}`}>
                <TableCell className="text-xs text-gray-500">{new Date(c.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <div className="font-heading font-bold text-black">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.email} · {c.phone || "—"}</div>
                </TableCell>
                <TableCell>{c.subject || "—"}</TableCell>
                <TableCell className="text-sm text-gray-700 max-w-md">{c.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ============================================================
   CONTENT EDITOR (unchanged behaviour, refreshed shell)
   ============================================================ */

function ContentEditor({
  contentMap, contentKeys, contentDirty, setContentDirty,
  contentSavingKey, section, setSection, onSaveKey, onSaveAll, onReset,
}) {
  const sectionKeys = useMemo(() => {
    const prefix = CONTENT_SECTIONS.find((s) => s.id === section)?.prefix || "";
    return contentKeys.filter((k) => k.startsWith(prefix));
  }, [contentKeys, section]);
  const dirtyCount = Object.keys(contentDirty).length;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
      <div className="flex items-start sm:items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-black">Site Content</h2>
          <p className="font-sub italic text-gray-500 text-sm mt-1">Edit any text shown on the public site. Changes go live immediately.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onReset} data-testid="content-reset" className="inline-flex items-center gap-2 border border-gray-200 hover:border-[#ED1C24] hover:text-[#ED1C24] px-3 py-2 rounded-lg text-xs uppercase tracking-wider font-bold">
            <RotateCcw size={14} /> Reset Defaults
          </button>
          <button onClick={onSaveAll} disabled={dirtyCount === 0} data-testid="content-save-all" className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-40 text-white px-4 py-2 rounded-lg text-xs uppercase tracking-wider font-bold">
            <Save size={14} /> Save {dirtyCount > 0 ? `(${dirtyCount})` : "All"}
          </button>
        </div>
      </div>
      <Divider className="my-4" />

      <div className="flex flex-wrap gap-2">
        {CONTENT_SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`content-section-${s.id}`}
            className={`px-4 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-md transition-colors ${
              section === s.id ? "bg-black text-white" : "bg-[#F6F7FB] text-black hover:bg-gray-200"
            }`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-4">
        {sectionKeys.map((key) => {
          const current = contentDirty[key] !== undefined ? contentDirty[key] : (contentMap[key] ?? "");
          const original = contentMap[key] ?? "";
          const dirty = contentDirty[key] !== undefined && contentDirty[key] !== original;
          const isLong = typeof original === "string" && (original.length > 120 || original.includes("\n"));
          const niceLabel = key.replace(/^[^.]+\./, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
          return (
            <div key={key} className="grid lg:grid-cols-12 gap-3 items-start border-b border-gray-100 pb-4">
              <div className="lg:col-span-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-black">{niceLabel}</Label>
                <p className="text-[10px] text-gray-400 mt-0.5 font-mono break-all">{key}</p>
              </div>
              <div className="lg:col-span-7">
                {isLong ? (
                  <Textarea value={current} onChange={(e) => setContentDirty((d) => ({ ...d, [key]: e.target.value }))} rows={3} data-testid={`content-input-${key}`} className="rounded-lg" />
                ) : (
                  <Input value={current} onChange={(e) => setContentDirty((d) => ({ ...d, [key]: e.target.value }))} data-testid={`content-input-${key}`} className="rounded-lg" />
                )}
              </div>
              <div className="lg:col-span-2 flex gap-2">
                <button onClick={() => onSaveKey(key)} disabled={!dirty || contentSavingKey === key} data-testid={`content-save-${key}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider font-bold border rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed border-gray-200 hover:border-[#00A651] hover:text-[#00A651]">
                  <Save size={12} /> {contentSavingKey === key ? "…" : (dirty ? "Save" : "Saved")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   LANDING PAGES ADMIN
   ============================================================ */

function LandingPagesAdmin({ pages, loading, onCreate, onUpdate, onDelete }) {
  const [draft, setDraft] = useState({ target_keyword: "", geo_city: "", sub_niche: "", program: "fha", cta_label: "Get Pre-Approved" });
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});

  const submit = (e) => {
    e.preventDefault();
    if (!draft.target_keyword.trim()) return;
    onCreate(draft);
    setDraft({ target_keyword: "", geo_city: "", sub_niche: "", program: "fha", cta_label: "Get Pre-Approved" });
  };
  const startEdit = (p) => {
    setEditingId(p.id);
    setEdit({
      target_keyword: p.target_keyword, geo_city: p.geo_city, sub_niche: p.sub_niche,
      headline: p.headline, subheadline: p.subheadline, body: p.body, cta_label: p.cta_label, program: p.program,
    });
  };
  const saveEdit = () => { onUpdate(editingId, edit); setEditingId(null); };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-xl text-black">Programmatic Landing Pages</h2>
          <p className="font-sub italic text-gray-500 text-sm mt-1">
            One row, one page. Each lives at <code className="text-[#00A651]">/lp/&lt;slug&gt;</code>.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-[#F6F7FB] border border-gray-100 p-5 rounded-2xl grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="lp-create-form">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider">Target Keyword</Label>
          <Input value={draft.target_keyword} onChange={(e) => setDraft({ ...draft, target_keyword: e.target.value })} placeholder="FHA Loans" data-testid="lp-create-keyword" className="mt-1 rounded-lg" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider">Geo City</Label>
          <Input value={draft.geo_city} onChange={(e) => setDraft({ ...draft, geo_city: e.target.value })} placeholder="Bartlett, TN" data-testid="lp-create-geo" className="mt-1 rounded-lg" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider">Sub-Niche</Label>
          <Input value={draft.sub_niche} onChange={(e) => setDraft({ ...draft, sub_niche: e.target.value })} placeholder="First-Time Homebuyer" data-testid="lp-create-niche" className="mt-1 rounded-lg" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider">Program</Label>
          <Select value={draft.program} onValueChange={(v) => setDraft({ ...draft, program: v })}>
            <SelectTrigger data-testid="lp-create-program" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["conventional", "fha", "va", "usda", "jumbo", "refinance"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={loading} data-testid="lp-create-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg">
            <Plus size={14} /> Create Page
          </button>
        </div>
      </form>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Slug</TableHead>
              <TableHead>Keyword · Geo</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Views</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pages.length === 0 && (<TableRow><TableCell colSpan={6} className="text-gray-500 italic">No pages yet — create your first one above.</TableCell></TableRow>)}
            {pages.map((p) => (
              <React.Fragment key={p.id}>
                <TableRow data-testid={`lp-row-${p.id}`}>
                  <TableCell>
                    <a href={`/lp/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-xs text-[#00A651] hover:underline">
                      /lp/{p.slug} <ExternalLink size={10} />
                    </a>
                  </TableCell>
                  <TableCell>
                    <div className="font-heading font-bold text-sm">{p.target_keyword}</div>
                    <div className="text-xs text-gray-500">{p.geo_city || "—"} {p.sub_niche ? `· ${p.sub_niche}` : ""}</div>
                  </TableCell>
                  <TableCell className="uppercase text-xs">{p.program}</TableCell>
                  <TableCell className="text-sm">{p.views || 0}</TableCell>
                  <TableCell>
                    <button onClick={() => onUpdate(p.id, { published: !p.published })} data-testid={`lp-toggle-${p.id}`}
                      className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold px-2 py-1 rounded-md ${
                        p.published ? "bg-[#00A651]/10 text-[#00A651]" : "bg-gray-100 text-gray-500"
                      }`}>
                      {p.published ? <Eye size={12} /> : <EyeOff size={12} />}
                      {p.published ? "Live" : "Draft"}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => (editingId === p.id ? setEditingId(null) : startEdit(p))} data-testid={`lp-edit-${p.id}`}
                      className="text-xs uppercase tracking-wider font-bold text-black hover:text-[#00A651] mr-3">
                      {editingId === p.id ? "Close" : "Edit"}
                    </button>
                    <button onClick={() => onDelete(p.id, p.slug)} data-testid={`lp-delete-${p.id}`}
                      className="text-xs uppercase tracking-wider font-bold text-[#ED1C24] hover:underline inline-flex items-center gap-1">
                      <Trash2 size={12} /> Delete
                    </button>
                  </TableCell>
                </TableRow>
                {editingId === p.id && (
                  <TableRow className="bg-[#F6F7FB]">
                    <TableCell colSpan={6}>
                      <div className="grid sm:grid-cols-2 gap-3 p-3" data-testid={`lp-edit-form-${p.id}`}>
                        <Field2 label="Target Keyword" v={edit.target_keyword} on={(v) => setEdit({ ...edit, target_keyword: v })} />
                        <Field2 label="Geo City" v={edit.geo_city} on={(v) => setEdit({ ...edit, geo_city: v })} />
                        <Field2 label="Sub-Niche" v={edit.sub_niche} on={(v) => setEdit({ ...edit, sub_niche: v })} />
                        <Field2 label="CTA Label" v={edit.cta_label} on={(v) => setEdit({ ...edit, cta_label: v })} />
                        <Field2 label="Headline" v={edit.headline} on={(v) => setEdit({ ...edit, headline: v })} full />
                        <Field2 label="Subheadline" v={edit.subheadline} on={(v) => setEdit({ ...edit, subheadline: v })} full />
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] font-bold uppercase tracking-wider">Body</Label>
                          <Textarea rows={3} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="mt-1 rounded-lg" />
                        </div>
                        <div className="sm:col-span-2 flex justify-end gap-2">
                          <button onClick={() => setEditingId(null)} className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-gray-300 rounded-lg">Cancel</button>
                          <button onClick={saveEdit} data-testid={`lp-save-${p.id}`} className="inline-flex items-center gap-1 px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#00A651] hover:bg-[#008f45] text-white rounded-lg">
                            <Save size={12} /> Save
                          </button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Field2({ label, v, on, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-[10px] font-bold uppercase tracking-wider">{label}</Label>
      <Input value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-1 rounded-lg" />
    </div>
  );
}
